'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import {
  AuthPageLogo,
  AuthPageWrapper,
} from '@/components/auth/auth-page-wrapper';
import {
  GoogleLogoMark,
  MicrosoftLogoMark,
  OauthBrandIconSlot,
} from '@/components/auth/oauth-provider-marks';
import { useLocale } from '@/components/common/locale-provider';
import { toast } from '@/components/shared/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { env } from '@/config/env';
import { useTranslations } from '@/hooks/use-translations';
import { getSession, signIn, useSession } from '@/lib/auth/auth-client';
import { clog } from '@/lib/logger/client';

interface LoginPageContentProps {
  canSelfServeSignup?: boolean;
}

type SocialProvider = 'microsoft' | 'google';

function getSocialSignInUrl(result: unknown): string | null {
  if (!result || typeof result !== 'object') return null;

  const maybeResult = result as {
    data?: { url?: unknown } | null;
    url?: unknown;
  };
  const url = maybeResult.data?.url ?? maybeResult.url;

  if (typeof url !== 'string') return null;

  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
      ? parsed.toString()
      : null;
  } catch {
    return null;
  }
}

function isInWebView2() {
  return typeof window !== 'undefined' && Boolean(window.chrome?.webview);
}

function getLoginDestination() {
  return typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('callbackUrl') ===
      '/signup/enterprise'
    ? '/signup/enterprise'
    : '/';
}

export function LoginPageContent({
  canSelfServeSignup = true,
}: LoginPageContentProps) {
  const router = useRouter();
  const { locale } = useLocale();
  const { t } = useTranslations(locale);

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isPollingSocialSignIn, setIsPollingSocialSignIn] = useState(false);

  const { data: session } = useSession();
  const canUseMicrosoft = env.NEXT_PUBLIC_MICROSOFT_SSO_ENABLED !== 'false';
  const canUseGoogle = Boolean(env.NEXT_PUBLIC_GOOGLE_CLIENT_ID);

  // Redirect if already logged in
  useEffect(() => {
    if (session?.user) {
      router.push(getLoginDestination());
    }
  }, [session, router]);

  useEffect(() => {
    if (!isPollingSocialSignIn) return;

    let isCancelled = false;
    const intervalId = window.setInterval(async () => {
      try {
        const result = await getSession();
        const nextSession =
          result && typeof result === 'object' && 'data' in result
            ? result.data
            : result;

        if (
          !isCancelled &&
          nextSession &&
          typeof nextSession === 'object' &&
          'user' in nextSession
        ) {
          isCancelled = true;
          window.clearInterval(intervalId);
          setIsPollingSocialSignIn(false);
          router.push(getLoginDestination());
        }
      } catch {
        // Keep polling while the provider flow is open.
      }
    }, 1000);

    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isPollingSocialSignIn, router]);

  // Check for SSO messages in URL and show toast
  useEffect(() => {
    // Wait for translations to be loaded
    if (!t) return;

    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get('sso_error');
    const ssoSuccess = params.get('sso_success');
    const error = params.get('error');
    const message = params.get('message');

    // Don't show toast if no relevant parameters
    if (!error && !ssoError && !ssoSuccess && !message) return;

    if (message === 'account-created') {
      const successMessage = t('auth.emailSignup.accountCreatedPleaseLogin');

      if (
        successMessage &&
        successMessage !== 'auth.emailSignup.accountCreatedPleaseLogin'
      ) {
        toast({
          type: 'success',
          description: successMessage,
        });
        window.history.replaceState({}, '', '/login');
      }
      return;
    }

    // Dropped here from a dead/expired app session (see lib/auth/session-expiry).
    // Neutral `info` style (not the red `error`, not the green `success`): a
    // session expiring is neither a failure nor an achievement — just a calm
    // nudge to sign in again for the — often non-technical — user.
    if (message === 'session-expired') {
      const sessionExpiredMessage = t('auth.sessionExpired');

      if (
        sessionExpiredMessage &&
        sessionExpiredMessage !== 'auth.sessionExpired'
      ) {
        toast.info(sessionExpiredMessage);
        window.history.replaceState({}, '', '/login');
      }
      return;
    }

    if (message === 'account-exists') {
      const accountExistsMessage = t('auth.socialSSO.accountAlreadyExists');

      if (
        accountExistsMessage &&
        accountExistsMessage !== 'auth.socialSSO.accountAlreadyExists'
      ) {
        toast({
          type: 'error',
          description: accountExistsMessage,
        });
        window.history.replaceState({}, '', '/login');
      }
      return;
    }

    // Handle legacy error parameter
    if (error === 'signup_disabled') {
      const signupDisabledMessage = t('auth.socialSSO.signupDisabled');

      if (
        signupDisabledMessage &&
        signupDisabledMessage !== 'auth.socialSSO.signupDisabled'
      ) {
        toast({
          type: 'error',
          description: signupDisabledMessage,
        });
        window.history.replaceState({}, '', '/login');
      }
      return;
    }

    // Handle SSO success messages
    if (ssoSuccess === 'account_created') {
      const message = t('auth.socialSSO.accountCreatedPleaseLogin');

      // Only show toast if translation is loaded (not empty)
      if (message && message !== 'auth.socialSSO.accountCreatedPleaseLogin') {
        toast({
          type: 'success',
          description: message,
        });
        window.history.replaceState({}, '', '/login');
      }
      return;
    }

    // Handle SSO error messages
    if (ssoError) {
      let errorMessage = '';

      switch (ssoError) {
        case 'email_mismatch':
          errorMessage = t('auth.microsoftSSO.emailMismatch');
          break;
        case 'domain_not_allowed':
          errorMessage = t('auth.microsoftSSO.domainNotAllowed');
          break;
        case 'account_exists':
          errorMessage = t('auth.socialSSO.accountAlreadyExists');
          break;
        case 'email_not_verified':
          errorMessage = t('auth.socialSSO.emailNotVerified');
          break;
        case 'organization_transfer_required':
          errorMessage = t('auth.microsoftSSO.organizationTransferRequired');
          break;
        case 'invitation_expired':
          errorMessage = t('auth.microsoftSSO.invitationExpired');
          break;
        case 'organization_not_found':
          errorMessage = t('auth.microsoftSSO.organizationNotFound');
          break;
        case 'domain_claimed': {
          const orgsParam = params.get('orgs');
          const orgList =
            orgsParam || t('auth.socialSSO.domainClaimedFallbackOrg');
          errorMessage = t('auth.socialSSO.domainClaimed', {
            orgs: orgList,
          });
          break;
        }
        case 'signup_failed':
        default:
          errorMessage = t('auth.socialSSO.signupFailed');
          break;
      }

      // Only show toast if translation is loaded (not empty)
      if (
        errorMessage &&
        !errorMessage.startsWith('auth.microsoftSSO.') &&
        !errorMessage.startsWith('auth.socialSSO.')
      ) {
        toast({
          type: 'error',
          description: errorMessage,
        });
        window.history.replaceState({}, '', '/login');
      }
    }
  }, [t]);

  const handleSocialSignIn = async (provider: SocialProvider) => {
    const isEmbedded = isInWebView2();
    let popupWindow: Window | null = null;

    if (isEmbedded) {
      popupWindow = window.open('', '_blank', 'popup,width=520,height=720');
      popupWindow?.focus();
    }

    try {
      const result = await signIn.social({
        provider,
        callbackURL: getLoginDestination(),
        errorCallbackURL: '/login',
        ...(isEmbedded ? { disableRedirect: true } : {}),
      });

      if (!isEmbedded) return;

      const url = getSocialSignInUrl(result);
      if (!url) {
        popupWindow?.close();
        throw new Error(`Missing ${provider} OAuth redirect URL`);
      }

      try {
        if (popupWindow && !popupWindow.closed) {
          popupWindow.location.href = url;
        }
      } catch {
        popupWindow = null;
      }

      if (!popupWindow || popupWindow.closed) {
        throw new Error(`Could not open ${provider} OAuth window`);
      }

      setIsPollingSocialSignIn(true);
    } catch (_error) {
      popupWindow?.close();
      // Fallback for client-side errors
      toast({
        type: 'error',
        description:
          provider === 'microsoft'
            ? t('auth.microsoftSignInError')
            : t('auth.googleSignInError'),
      });
    }
  };

  const handleMicrosoftSignIn = () => handleSocialSignIn('microsoft');

  const handleGoogleSignIn = () => handleSocialSignIn('google');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const emailValue = formData.get('email') as string;
    const passwordValue = formData.get('password') as string;

    try {
      // Sign in on the client - cookies are automatically set
      const result = (await signIn.email({
        email: emailValue,
        password: passwordValue,
      })) as { error?: { message?: string } | string } | undefined;

      clog.debug('Sign in result:', result);

      // Check for error in Better Auth response format
      if (result?.error) {
        const errorMessage =
          typeof result.error === 'string'
            ? result.error
            : result.error?.message || 'Invalid credentials!';

        toast({
          type: 'error',
          description: errorMessage,
        });
        setIsLoading(false);
        return;
      }

      // Success!
      clog.debug('Sign in successful, redirecting...');
      setIsSuccessful(true);

      // Simple client-side navigation - middleware will handle auth
      router.push(getLoginDestination());
    } catch (error: any) {
      // Better Auth throws errors for network/server issues
      clog.error('Sign in error:', error);
      const errorMessage =
        error?.message ||
        error?.body?.message ||
        'An error occurred during sign in';

      toast({
        type: 'error',
        description: errorMessage,
      });
      setIsLoading(false);
    }
  };

  return (
    <AuthPageWrapper>
      <AuthPageLogo title={t('auth.signIn')} />
      <div className="flex w-full flex-col gap-8">
        <form
          onSubmit={handleSubmit}
          method="post"
          action=""
          className="flex flex-col gap-4"
        >
          <div className="flex flex-col gap-2">
            <Input
              id="email"
              name="email"
              type="email"
              placeholder={t('auth.emailPlaceholder')}
              required
              autoFocus
              className="bg-white dark:bg-muted"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Input
              id="password"
              name="password"
              type="password"
              placeholder={t('placeholders.password')}
              required
              className="bg-white dark:bg-muted"
            />
          </div>
          <div className="flex justify-end">
            <Link
              href="/forgot-password"
              className="text-sm text-muted-foreground hover:text-primary hover:underline"
            >
              {t('auth.forgotPassword.link')}
            </Link>
          </div>
          <div className="flex flex-col gap-4">
            <Button
              type="submit"
              disabled={isLoading || isSuccessful}
              className="mt-2 w-full"
            >
              {isLoading
                ? t('auth.signingIn')
                : isSuccessful
                  ? t('auth.success')
                  : t('auth.signInButton')}
            </Button>
          </div>
        </form>

        {(canUseMicrosoft || canUseGoogle) && (
          <div className="flex flex-col gap-3">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white dark:bg-card px-2 text-muted-foreground">
                  {t('auth.orContinueWith')}
                </span>
              </div>
            </div>

            {canUseMicrosoft ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleMicrosoftSignIn}
                className="w-full justify-start"
              >
                <OauthBrandIconSlot>
                  <MicrosoftLogoMark />
                </OauthBrandIconSlot>
                {t('auth.signInWithMicrosoft')}
              </Button>
            ) : null}

            {canUseGoogle ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleGoogleSignIn}
                className="w-full justify-start"
              >
                <OauthBrandIconSlot>
                  <GoogleLogoMark />
                </OauthBrandIconSlot>
                {t('auth.signInWithGoogle')}
              </Button>
            ) : null}
          </div>
        )}
      </div>
      {canSelfServeSignup && (
        <div className="flex justify-center gap-1 text-sm text-muted-foreground">
          <p>{t('auth.noAccount')}</p>
          <Link
            href="/signup"
            className="font-medium text-primary hover:underline"
          >
            {t('auth.signUp')}
          </Link>
        </div>
      )}
    </AuthPageWrapper>
  );
}
